<template>
    <MainComponent :id="anchorsPageProfile.appraisalsSection" :class="{'appraisal-section--mf': mf}">
        <template #title>
            <h2 class="appraisal-section__title">{{ t('profile.resume.appraisals.title') }}</h2>
        </template>
        <template #title-action>
            <VJoyWrapper no-margin direction="row" justify="flex-end" class="appraisal-section__title-action">
                <LazyTranslateButton v-if="isTranslationActivated && hasAppraisals" section="appraisal" />
                <AppraisalSortMenu
                    v-if="visibleAppraisals.length > 1"
                    v-model:selected-sort="selectedSort"
                    class="appraisal-section__sort-menu--desktop"
                />
            </VJoyWrapper>
        </template>
        <template v-if="appraisals" #content>
            <EmptyAppraisalsEditMode v-if="!hasAppraisals && isEditionEnabled" />
            <template v-else-if="hasAppraisals">
                <VJoyWrapper direction="row" justify="flex-start" align="flex-start" class="appraisal-section__rating-container">
                    <OverallRating :ratings="appraisals.ratings" :appraisal-counter="appraisals.numberOfAppraisalsWithRating || 0" />
                    <AppraisalSortMenu
                        v-if="visibleAppraisals.length > 1"
                        v-model:selected-sort="selectedSort"
                        class="appraisal-section__sort-menu--mobile"
                    />
                    <VJoyWrapper
                        direction="column"
                        row-gap="4"
                        :no-margin="true"
                        align="flex-start"
                        justify="flex-start"
                        class="appraisal-section__list"
                    >
                        <NoDisplayedAppraisals v-if="noAppraisalToDisplay" :freelancer-name="profile?.firstname" />
                        <AppraisalCard
                            v-for="appraisal in visibleAppraisals"
                            v-else
                            :key="appraisal.appraisalId"
                            :appraisal="appraisal"
                            :is-edit-mode="isEditionEnabled"
                            :mf="mf"
                            class="appraisal-section__item"
                            @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
                        />
                        <HiddenAppraisalIndicator
                            v-if="profile && !isEditionEnabled && hiddenAppraisalsCounter > 0 && !noAppraisalToDisplay"
                            :account-firstname="profile.firstname"
                            :hidden-counter="hiddenAppraisalsCounter"
                            :hidden-by-client="false"
                        />
                        <HiddenAppraisalIndicator
                            v-if="isEditionEnabled && hiddenByClientCounter > 0"
                            :hidden-counter="hiddenByClientCounter"
                            :hidden-by-client="true"
                        />
                        <VJoyText
                            v-if="hiddenByAnyoneCounter > 0 && !noAppraisalToDisplay"
                            preset="primary-300"
                            tag-name="span"
                            class="appraisal-section__private-count"
                        >
                            {{ t('profile.section.appraisals.hidden.count', {count: hiddenByAnyoneCounter}) }}
                        </VJoyText>
                    </VJoyWrapper>
                </VJoyWrapper>
                <ShowMoreByItemCount
                    :total="sortedAppraisals.length"
                    :label="t('profile.section.appraisals.show.all.label.v2', {count: numberOfAppraisal})"
                    :limit="APPRAISAL_LIMIT_DISPLAYED"
                    test-id="appraisals-show-more"
                    :locked="{
                        allowHref: !mf,
                        componentName: 'profile-appraisals-section',
                        targetId: anchorsPageProfile.appraisalsSection,
                        label: t('profile.section.appraisals.show.all.label.v2', {count: numberOfAppraisal}),
                    }"
                    @show-more-clicked-when-locked="$emit('showMoreClickedWhenLocked')"
                    @expand="isExpanded = true"
                    @collapse="isExpanded = false"
                />
            </template>
        </template>
    </MainComponent>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {LazyTranslateButton} from '#components';
    import {computed, onMounted, onUnmounted, ref, useAuth, useTranslation} from '#imports';
    import {VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import AppraisalCard from '~/features/appraisals/ui/AppraisalCard.vue';
    import AppraisalSortMenu, {type AppraisalSortOption} from '~/features/appraisals/ui/AppraisalSortMenu.vue';
    import EmptyAppraisalsEditMode from '~/features/appraisals/ui/EmptyAppraisalsEditMode.vue';
    import HiddenAppraisalIndicator from '~/features/appraisals/ui/HiddenAppraisalIndicator.vue';
    import NoDisplayedAppraisals from '~/features/appraisals/ui/NoDisplayedAppraisals.vue';
    import OverallRating from '~/features/appraisals/ui/OverallRating.vue';
    import {bestRatingSorting, clientFirstSorting, mostRecentSorting, mostRelevantSorting} from '~/features/appraisals/utils/appraisalSorting';
    import {useProfileStore} from '~/store/profileStore';
    import {TranslationDirection, useTranslationStore} from '~/store/translation/translationStore';
    import type {Appraisal} from '~/types/domain/Appraisals';

    type Props = {
        mf?: boolean;
    };

    defineEmits(['showMoreClickedWhenLocked', 'seeMoreClickedWhenLocked']);

    const {mf = false} = defineProps<Props>();

    const APPRAISAL_LIMIT_DISPLAYED = 2;
    const {appraisals, profile} = storeToRefs(useProfileStore());
    const {isEditionEnabled} = useProfilePageViewMode();
    const {user} = storeToRefs(useAuth());
    const numberOfAppraisal = computed(() => appraisals.value?.appraisals.length);
    const isExpanded = ref(false);

    const hiddenAppraisalsCounter = computed(() => (appraisals.value?.appraisals.filter((appraisal: Appraisal) => appraisal.hidden) ?? []).length);

    const hiddenByClientCounter = computed(
        () =>
            (
                appraisals.value?.appraisals.filter(
                    (appraisal: Appraisal) => appraisal.hiddenByClient && appraisal.clientAccountId !== user.value?.id,
                ) ?? []
            ).length,
    );

    const hiddenByAnyoneCounter = computed(
        () => (appraisals.value?.appraisals.filter((appraisal: Appraisal) => appraisal.hidden || appraisal.hiddenByClient) ?? []).length,
    );

    const visibleAppraisals = computed(() => {
        const appraisalsList = isExpanded.value ? sortedAppraisals.value : sortedAppraisals.value.slice(0, APPRAISAL_LIMIT_DISPLAYED);
        return appraisalsList.filter((appraisal: Appraisal) => {
            if (appraisal.hiddenByClient && appraisal.clientAccountId !== user.value?.id) {
                return false;
            }
            if (!isEditionEnabled.value && appraisal.hidden) {
                return false;
            }
            return true;
        });
    });

    const noAppraisalToDisplay = computed(
        () => visibleAppraisals.value.length === 0 && !isEditionEnabled.value && (numberOfAppraisal.value ?? 0) > 0,
    );

    const appraisalsWithComment = computed(() => appraisals.value?.appraisals.filter((appraisal: Appraisal) => appraisal.comment) ?? []);
    const hasAppraisals = computed<boolean>(() => (appraisals.value && appraisals.value!.appraisals.length > 0) || false);

    // Sorting features
    const selectedSort = ref<AppraisalSortOption>('most-relevant');
    const sortedAppraisals = computed(() => {
        if (!appraisals.value) return [];

        let sorted: Appraisal[];
        switch (selectedSort.value) {
            case 'most-relevant':
                sorted = mostRelevantSorting([...appraisals.value.appraisals]);
                break;
            case 'best-rating':
                sorted = bestRatingSorting([...appraisals.value.appraisals]);
                break;
            case 'most-recent':
                sorted = mostRecentSorting([...appraisals.value.appraisals]);
                break;
            default:
                sorted = [...appraisals.value.appraisals];
        }
        return clientFirstSorting(sorted, user.value?.id);
    });

    //Translation features
    const {t} = useTranslation();
    const {isTranslationActivated} = storeToRefs(useTranslationStore());
    const {translateAll, replaceTranslationContent} = useTranslationStore();
    const translationSection = 'appraisals';
    const translationField = 'comment';

    async function translateAppraisals() {
        const appraisalCommentRequests = appraisalsWithComment.value.map((appraisal: Appraisal, index: number) => {
            return {
                section: translationSection,
                field: translationField,
                index,
                originalContent: appraisal.comment ?? ' ',
            };
        });

        await translateAll(appraisalCommentRequests);
        replaceTranslationContent(translationSection, translationField, TranslationDirection.TRANSLATE, switchTranslation);
    }

    function restoreAppraisals() {
        replaceTranslationContent(translationSection, translationField, TranslationDirection.RESTORE, switchTranslation);
    }

    function switchTranslation(content: string, index: number) {
        appraisalsWithComment.value[index].comment = content;
    }

    onMounted(() => {
        eventBus.on('translation:request', translateAppraisals);
        eventBus.on('translation:restore', restoreAppraisals);
    });

    onUnmounted(() => {
        eventBus.off('translation:request', translateAppraisals);
        eventBus.off('translation:restore', restoreAppraisals);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .appraisal-section {
        &--mf {
            border: none !important;
            border-radius: 0 !important;
            border-bottom: 1px solid var(--joy-color-neutral-30) !important;
            padding-bottom: var(--joy-core-spacing-8);
            margin-bottom: 0 !important;
            .appraisal-section__title {
                font-size: var(--joy-font-size-primary-400);
            }
        }

        &__sort-menu {
            &--mobile {
                display: block;
                width: 100%;
                order: 2;

                @include joy.screen_xs(min) {
                    display: none;
                }
            }

            &--desktop {
                display: none;

                @include joy.screen_xs(min) {
                    display: block;
                }
            }
        }

        &__rating-container {
            display: flex;
            gap: var(--joy-core-spacing-5);
            align-items: flex-start;
            flex-direction: column;

            @include joy.screen_xs(min) {
                flex-direction: row;
            }
        }

        &__list {
            width: 100%;
            min-width: 0;
            order: 3;

            @include joy.screen_xs(min) {
                flex: 1;
                width: auto;
                order: 0;
            }
        }

        &__item {
            padding-bottom: var(--joy-core-spacing-6);
            margin-bottom: var(--joy-core-spacing-2);
            border-bottom: 1px solid var(--joy-color-neutral-30);

            &:last-child {
                border-bottom: none;
                margin-bottom: 0;
                padding-bottom: 0;
            }
        }
    }
</style>
